import { uiText } from '../i18n/uiText';
import { Link } from 'react-router-dom';
import api from '../api';
import { formatStatus } from '../utils/formatters';
import QualificationGrants from '../components/qualification/QualificationGrants';
import React, { useEffect, useMemo, useState, useRef } from 'react';
import { auditorPoolApi, usersApi, auditsApi, auditAssignmentsApi, getApiErrorMessage } from '../api';
import { useAuth } from '../store/AuthContext';
import useDraftBuffer from '../hooks/useDraftBuffer';
import FormDraftNotice from '../components/common/FormDraftNotice';
import { usePrompt } from '../store/ConfirmContext';
import { useToast } from '../store/ToastContext';
import {
  Users, Tag, CheckCircle, Clock, Calendar, Star,
  Briefcase, UserCheck, UserPlus, Filter, Search, Plus, X,
} from 'lucide-react';
import PageIntro from '../components/ui/PageIntro';
import Dialog from '../components/common/Dialog';
import FormField, { TextInput, SelectInput, TextArea } from '../components/common/FormField';

/* ─── Specialization catalogue ─────────────────────────── */
const SPECIALIZATIONS = [
  { value: 'supplier_audit',     label: uiText("Аудит поставщиков") },
  { value: 'process_audit',      label: uiText("Аудит процессов") },
  { value: 'quality_systems',    label: uiText("Системы менеджмента качества") },
  { value: 'food_safety',        label: uiText("Безопасность пищевой продукции") },
  { value: 'env_audit',          label: uiText("Экологический аудит") },
  { value: 'safety_audit',       label: uiText("Охрана труда и безопасность") },
  { value: 'financial_audit',    label: uiText("Финансовый аудит") },
  { value: 'it_audit',           label: uiText("ИТ-аудит") },
  { value: 'procurement',        label: uiText("Закупочная деятельность") },
  { value: 'construction',       label: uiText("Строительство и эксплуатация") },
  { value: 'extended_audit',     label: uiText("Аудиты с расширенным регламентом") },
  { value: 'compliance',         label: uiText("Комплаенс / нормативное соответствие") },
  { value: 'internal_control',   label: uiText("Внутренний контроль") },
  { value: 'risk_management',    label: uiText("Управление рисками") },
];

const AUDITOR_TYPES = [
  { value: 'staff',      label: uiText("Штатный аудитор"),    color: '#1d4ed8', bg: '#dbeafe' },
  { value: 'contracted', label: uiText("Привлекаемый"),        color: '#a16207', bg: '#fef9c3' },
];

const typeLabel = (t) => AUDITOR_TYPES.find(x=>x.value===t) || { label: t||uiText("Штатный"), color:'#1d4ed8', bg:'#dbeafe' };

function SpecTag({ value }) {
  const s = SPECIALIZATIONS.find(x=>x.value===value);
  return (
    <span style={{ display:'inline-flex', alignItems:'center', gap:4, background:'#f0f9ff', color:'#0369a1', border:'1px solid #bae6fd', borderRadius:999, padding:'2px 8px', fontSize:11, fontWeight:600 }}>
      <Tag size={10}/>{s?.label || value}
    </span>
  );
}

function AuditorCard({ profile, audits, onAssign, canManage, currentUserId, onApply, assignmentBusy, onOpen }) {
  const [expanded, setExpanded] = useState(false);
  const typ = typeLabel(profile.auditor_type);
  const isMe = profile.user_id === currentUserId;
  const specsArr = Array.isArray(profile.specializations)
    ? profile.specializations
    : (profile.specializations||'').split(',').map(s=>s.trim()).filter(Boolean);

  return (
    <div className="card" style={{ marginBottom:10 }}>
      <div className="card-body">
        <div style={{ display:'flex', gap:14, alignItems:'flex-start', flexWrap:'wrap' }}>
          <div className="user-avatar" style={{ width:44, height:44, fontSize:15, flexShrink:0 }}>
            {profile.avatar_data ? <img className="auditor-avatar-image" src={profile.avatar_data} alt="" /> : profile.full_name?.split(' ').map(w=>w[0]).slice(0,2).join('')}
          </div>
          <div style={{ flex:1, minWidth:160 }}>
            <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
              <span style={{ fontWeight:700, fontSize:15 }}>{profile.full_name}</span>
              <span style={{ fontSize:11, background:typ.bg, color:typ.color, borderRadius:999, padding:'2px 8px', fontWeight:700 }}>{typ.label}</span>
              {isMe && <span className="badge badge-green">{uiText("Это вы")}</span>}
            </div>
            {profile.job_title && <div style={{ fontSize:12, color:'#666', marginTop:2 }}><Briefcase size={11} style={{display:'inline',marginRight:3}}/>{profile.job_title}</div>}
            {profile.experience_years != null && (
              <div style={{ fontSize:12, color:'#666' }}><Star size={11} style={{display:'inline',marginRight:3}}/>{profile.experience_years}{uiText(" лет опыта")}</div>
            )}
          </div>
          <div className="auditor-card-actions" style={{ display:'flex', gap:6, flexShrink:0, flexWrap:'wrap', alignItems:'center' }}>
            {profile.available_from && (
              <span style={{ fontSize:11, color:'#15803d', background:'#dcfce7', borderRadius:999, padding:'2px 8px', display:'flex', alignItems:'center', gap:4 }}>
                <CheckCircle size={10}/>{uiText(" Свободен с ")}{String(profile.available_from).slice(0,10)}
              </span>
            )}
            {!profile.available_from && (
              <span style={{ fontSize:11, color:'#666', background:'#f5f5f5', borderRadius:999, padding:'2px 8px', display:'flex', alignItems:'center', gap:4 }}>
                <Clock size={10}/>{uiText(" Доступность не указана")}</span>
            )}
            <button type="button" className="ap-button ap-button--secondary ap-control-action" onClick={() => onOpen(profile)}>
              {uiText("Карточка аудитора")}
            </button>
            {canManage && (
              <select className="form-control auditor-card-assignment" style={{ fontSize:12, padding:'4px 8px', height:'auto', width:'auto', minWidth:180 }}
                aria-label={uiText("Назначить {v0} на аудит", {v0: (profile.full_name || 'аудитора')})} disabled={assignmentBusy}
                defaultValue="" onChange={e=>{ if(e.target.value) { onAssign(profile.user_id, e.target.value); e.target.value=''; } }}>
                <option value="">{uiText("Назначить на аудит…")}</option>
                {audits.filter(a=>!['completed', 'closed'].includes(a.status)).map(a=>(
                  <option key={a.id} value={a.id}>{a.title.slice(0,50)}</option>
                ))}
              </select>
            )}
            {profile.auditor_type==='contracted' && !isMe && !canManage && (
              <button type="button" className="ap-button ap-button--secondary ap-control-action" onClick={()=>onApply(profile.user_id)}><UserPlus size={15}/>{uiText(" Подать заявку")}</button>
            )}
          </div>
        </div>

        {expanded && (
          <div style={{ marginTop:14, paddingTop:14, borderTop:'1px solid var(--premium-border)', display:'grid', gap:10 }}>
            {!specsArr.length && !profile.certifications && !profile.about && !profile.availability_note && (
              <div className="workspace-muted">{uiText("Дополнительные сведения пока не заполнены.")}</div>
            )}
            {specsArr.length > 0 && (
              <div>
                <div style={{ fontSize:12, fontWeight:600, color:'#666', marginBottom:6 }}>{uiText("Специализации")}</div>
                <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
                  {specsArr.map(s=><SpecTag key={s} value={s}/>)}
                </div>
              </div>
            )}
            {profile.certifications && (
              <div>
                <div style={{ fontSize:12, fontWeight:600, color:'#666', marginBottom:4 }}>{uiText("Сертификаты / квалификация")}</div>
                <div style={{ fontSize:13 }}>{profile.certifications}</div>
              </div>
            )}
            {profile.about && (
              <div>
                <div style={{ fontSize:12, fontWeight:600, color:'#666', marginBottom:4 }}>{uiText("О себе")}</div>
                <div style={{ fontSize:13 }}>{profile.about}</div>
              </div>
            )}
            {profile.availability_note && (
              <div>
                <div style={{ fontSize:12, fontWeight:600, color:'#666', marginBottom:4 }}><Calendar size={11} style={{display:'inline',marginRight:3}}/>{uiText("Комментарий по доступности")}</div>
                <div style={{ fontSize:13 }}>{profile.availability_note}</div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/* ─── Profile edit modal ─────────────────────────────────── */
export function ProfileModal({ profile, onClose, onSaved }) {
  const { user } = useAuth();
  const canEdit = user?.role === 'admin' || user?.id === profile.user_id;
  const [tab, setTab] = useState('profile');
  const [grantsBusy, setGrantsBusy] = useState(false);
  const [grants, setGrants] = useState(null);
  const [grantsError, setGrantsError] = useState('');
  const loadGrants = async () => { try { const response = await api.get('/qualification/grants'); setGrants(response.data); setGrantsError(''); } catch (e) { setGrantsError(getApiErrorMessage(e)); } };
  useEffect(() => { if (user?.role === 'admin' && process.env.REACT_APP_ENABLE_QUALIFICATION === 'true') loadGrants(); }, []);
  const [initialForm] = useState({
    full_name: profile?.full_name || '',
    job_title: profile?.job_title || '',
    avatar_data: profile?.avatar_data || '',
    auditor_type: profile?.auditor_type || 'staff',
    specializations: Array.isArray(profile?.specializations) ? profile.specializations : (profile?.specializations || '').split(',').map(s => s.trim()).filter(Boolean),
    experience_years: profile?.experience_years ?? '',
    certifications: profile?.certifications || '',
    about: profile?.about || '',
    available_from: profile?.available_from ? String(profile.available_from).slice(0, 10) : '',
    availability_note: profile?.availability_note || '',
  });
  const [saving, setSaving] = useState(false);
  const buffer = useDraftBuffer(user?.id, profile.user_id, 'auditor-profile');
  const form = buffer.values.form || initialForm;
  const setForm = update => { if (!saving && !buffer.recovery) buffer.edit('form', typeof update === 'function' ? update(buffer.values.form || initialForm) : update); };
  const savingRef = useRef(false);
  const [error, setError] = useState('');
  const update = key => event => setForm(previous => ({ ...previous, [key]: event.target.value }));
  const toggleSpec = value => setForm(previous => ({
    ...previous, specializations: previous.specializations.includes(value)
      ? previous.specializations.filter(item => item !== value) : [...previous.specializations, value],
  }));
  const handleSave = async event => {
    event.preventDefault();
    if (!canEdit || savingRef.current || buffer.recovery) return;
    const snapshot = buffer.snapshot();
    savingRef.current = true;
    setSaving(true); setError('');
    try {
      await auditorPoolApi.updateProfile(profile.user_id, {
        ...form, experience_years: form.experience_years !== '' ? Number(form.experience_years) : null,
      });
      buffer.acknowledge(snapshot, { committed: true });
      if (buffer.active) await onSaved();
    } catch (error) { setError(getApiErrorMessage(error)); }
    finally { savingRef.current = false; setSaving(false); }
  };
  return (
    <Dialog label={uiText("Профиль аудитора")} onClose={onClose} closeDisabled={saving || grantsBusy}>
      <div className="ap-dialog__header">
        <div><h2 className="ap-dialog__title" data-dialog-heading tabIndex={-1}><UserCheck size={20}/>{uiText("Профиль аудитора")}</h2>
          <p className="ap-dialog__description">{profile?.full_name}</p></div>
        <button className="ap-dialog__close" type="button" aria-label={uiText("Закрыть")} disabled={saving || grantsBusy} onClick={onClose}><X size={18}/></button>
      </div>
      <div className="auditor-profile-tabs" aria-label={uiText('Разделы профиля')}>
        {[["profile", "Профиль"], ["audits", "Аудиты"], ...(user?.role === 'admin' ? [["rights", "Права"]] : [])].map(([key, label]) => <button type="button" className="ap-button ap-button--secondary" key={key} aria-pressed={tab === key} disabled={saving || grantsBusy} onClick={() => setTab(key)}>{uiText(label)}</button>)}
      </div>
      {tab === 'profile' && <form onSubmit={handleSave}>
        <div className="ap-dialog__body">
          <FormDraftNotice buffer={buffer} label={uiText("Профиль аудитора")} disabled={saving} />
          <fieldset className="ap-fieldset" disabled={!canEdit || saving || !!buffer.recovery}>
            <div className="auditor-profile-identity">
              <div className="user-avatar auditor-profile-avatar">{form.avatar_data ? <img className="auditor-avatar-image" src={form.avatar_data} alt={uiText('Фото аудитора')} /> : profile.full_name?.split(' ').map(w => w[0]).slice(0,2).join('')}</div>
              <FormField label={uiText('Фото аудитора')} hint={uiText('JPEG, PNG или WebP, до 250 КБ.')}><input type="file" accept="image/jpeg,image/png,image/webp" onChange={e => {
                const file = e.target.files?.[0]; if (!file) return;
                if (file.size > 250 * 1024) { setError(uiText('Фото должно быть не больше 250 КБ.')); e.target.value = ''; return; }
                const reader = new FileReader(); reader.onload = () => { setForm(p => ({ ...p, avatar_data: reader.result })); setError(''); }; reader.readAsDataURL(file);
              }}/></FormField>{form.avatar_data && <button type="button" className="ap-button ap-button--secondary" onClick={() => setForm(p => ({ ...p, avatar_data: '' }))}>{uiText('Убрать фото')}</button>}
            </div>
            <div className="ap-form-grid"><FormField label={uiText('ФИО')}><TextInput required value={form.full_name} onChange={update('full_name')} /></FormField><FormField label={uiText('Должность')}><TextInput value={form.job_title} onChange={update('job_title')} /></FormField></div>
            <div className="ap-form-grid ap-form-grid--three">
              <FormField label={uiText("Тип аудитора")}><SelectInput value={form.auditor_type} onChange={update('auditor_type')}>
                {AUDITOR_TYPES.map(type => <option key={type.value} value={type.value}>{type.label}</option>)}
              </SelectInput></FormField>
              <FormField label={uiText("Лет опыта")}><TextInput type="number" min="0" value={form.experience_years} onChange={update('experience_years')}/></FormField>
              <FormField label={uiText("Свободен с")}><TextInput type="date" value={form.available_from} onChange={update('available_from')}/></FormField>
            </div>
            <fieldset className="ap-choice-group">
              <legend>{uiText("Специализации")}</legend>
              <div className="ap-choice-grid">
                {SPECIALIZATIONS.map(spec => <label className="ap-choice" key={spec.value}>
                  <input className="ap-check" type="checkbox" checked={form.specializations.includes(spec.value)} onChange={() => toggleSpec(spec.value)}/>
                  <span>{spec.label}</span>
                </label>)}
              </div>
            </fieldset>
            <FormField label={uiText("Сертификаты / квалификация")}><TextArea rows={3} value={form.certifications} onChange={update('certifications')}/></FormField>
            <FormField label={uiText("О себе")}><TextArea rows={3} value={form.about} onChange={update('about')}/></FormField>
            <FormField label={uiText("Комментарий по доступности")}><TextInput value={form.availability_note} onChange={update('availability_note')} placeholder={uiText("Например: доступен для командировок 3–4 раза в месяц")}/></FormField>
          </fieldset>
          {error && <div className="ap-dialog__alert" role="alert">{error}</div>}
        </div>
        <div className="ap-dialog__footer">
          <button type="button" className="ap-button" disabled={saving} onClick={onClose}>{uiText("Отмена")}</button>
          {canEdit && <button type="submit" className="ap-button ap-button--primary" disabled={saving}>{saving ? uiText("Сохранение…") : uiText("Сохранить профиль")}</button>}
        </div>
      </form>}
      {tab === 'audits' && <section className="ap-dialog__body auditor-profile-history"><h3>{uiText('Аудиты сотрудника')}</h3><p className="workspace-muted">{uiText('Текущие и завершённые аудиты, доступные вам для просмотра.')}</p>
        {['active', 'completed'].map(group => { const rows = (profile.audits || []).filter(a => ['closed', 'completed', 'cancelled'].includes(a.status) === (group === 'completed')); return <div key={group}><h4>{group === 'active' ? uiText('В работе и запланированы') : uiText('Завершены и отменены')} · {rows.length}</h4>{rows.length ? <ul>{rows.map(a => <li key={a.id}><Link to={`/audits/${a.id}`}>{a.title}</Link><span>{formatStatus(a.status)}</span></li>)}</ul> : <p className="workspace-muted">{uiText('Нет аудитов')}</p>}</div>; })}
      </section>}
      {tab === 'rights' && user?.role === 'admin' && <section className="ap-dialog__body auditor-profile-rights"><h3>{uiText('Права и доступ')}</h3><p>{uiText('Роль')}: {profile.role === 'admin' ? uiText('Администратор') : uiText('Аудитор')}. {uiText('Право редактирования конкретного аудита зависит от назначения в его команду.')}</p>
        <Link className="ap-button ap-button--secondary" to={`/users?q=${encodeURIComponent(profile.username || profile.full_name || '')}`}>{uiText('Управление учётной записью')}</Link>
        {grantsError && <p role="alert">{grantsError}</p>}
        {grants && <QualificationGrants users={[{ ...profile, id: profile.user_id }]} grants={grants} labels={{methodology_edit: uiText('Редактировать методологии'), methodology_publish: uiText('Публиковать методологии'), assessment_verify: uiText('Подтверждать оценки и сравнивать'), need_edit: uiText('Готовить потребности'), need_publish: uiText('Фиксировать требования задач'), engagement_decide: uiText('Принимать решения о привлечении'), structure_edit: uiText('Структура: редактировать подразделения'), resource_edit: uiText('Ресурсы: вести реестр')}} onSaved={loadGrants} onBusyChange={setGrantsBusy} />}
      </section>}
      {tab !== 'profile' && <div className="ap-dialog__footer"><button type="button" className="ap-button ap-button--secondary" disabled={grantsBusy} onClick={onClose}>{uiText('Закрыть')}</button></div>}
    </Dialog>
  );
}

/* ─── Main page ─────────────────────────────────────────── */
export default function AuditorPoolPage() {
  const prompt = usePrompt();
  const { showToast } = useToast();
  const assigningRef = useRef(false);
  const [assignmentBusy, setAssignmentBusy] = useState(false);
  const { user } = useAuth();
  const isAdmin   = user?.role === 'admin';
  const isAuditor = user?.role === 'auditor';

  const [profiles, setProfiles] = useState([]);
  const [audits, setAudits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [specFilter, setSpecFilter] = useState('');
  const [editingProfile, setEditingProfile] = useState(null);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const [poolRes, auditsRes] = await Promise.all([
        auditorPoolApi.list(),
        (isAdmin||isAuditor) ? auditsApi.list().catch(()=>({data:[]})) : Promise.resolve({data:[]}),
      ]);
      setProfiles(Array.isArray(poolRes.data) ? poolRes.data : []);
      setAudits(Array.isArray(auditsRes.data) ? auditsRes.data : []);
    } catch (e) {
      setError(getApiErrorMessage(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return profiles.filter(p => {
      const specsArr = Array.isArray(p.specializations) ? p.specializations : (p.specializations||'').split(',').map(s=>s.trim()).filter(Boolean);
      const matchSearch = !q || (p.full_name||'').toLowerCase().includes(q) || (p.job_title||'').toLowerCase().includes(q) || specsArr.some(s=>[s, SPECIALIZATIONS.find(item => item.value === s)?.label || ''].some(label => label.toLowerCase().includes(q)));
      const matchType = typeFilter==='all' || p.auditor_type===typeFilter;
      const matchSpec = !specFilter || specsArr.includes(specFilter);
      return matchSearch && matchType && matchSpec;
    });
  }, [profiles, search, typeFilter, specFilter]);

  const myProfile = profiles.find(p => p.user_id === user?.id);

  const handleAssign = async (auditorUserId, auditId) => {
    if (assigningRef.current) return;
    assigningRef.current = true; setAssignmentBusy(true);
    try {
      await auditAssignmentsApi.assign(auditId, { auditor_user_ids: [auditorUserId], append_only: true });
      showToast({ type: 'success', message: uiText("Аудитор добавлен в команду. Остальные назначения сохранены.") });
      await load();
    } catch (e) { showToast({ type: 'error', message: getApiErrorMessage(e) }); }
    finally { assigningRef.current = false; setAssignmentBusy(false); }
  };

  const handleApply = async (auditorUserId) => {
    const auditId = await prompt(uiText("Введите номер аудита, на который хотите подать заявку."), {
      title: uiText("Заявка на участие в аудите"),
      placeholder: uiText("Например, 125"),
      inputType: 'number',
      confirmLabel: uiText("Подать заявку"),
      required: true,
    });
    if (!auditId) return;
    try {
      await auditorPoolApi.applyToAudit(Number(auditId), { auditor_user_id: auditorUserId });
      alert(uiText("Заявка подана."));
    } catch (e) { alert(getApiErrorMessage(e)); }
  };

  const handleEditMyProfile = () => {
    const p = myProfile || { user_id: user?.id, full_name: user?.full_name };
    openProfile(p);
  };
  const openProfile = async p => { try { const response = await auditorPoolApi.getProfile(p.user_id); setEditingProfile(response.data); } catch (e) { setError(getApiErrorMessage(e)); } };

  const summary = useMemo(() => ({
    total:      profiles.length,
    staff:      profiles.filter(p=>p.auditor_type==='staff').length,
    contracted: profiles.filter(p=>p.auditor_type==='contracted').length,
    available:  profiles.filter(p=>p.available_from).length,
  }), [profiles]);

  return (
    <div>
      <PageIntro
        title={uiText("Аудиторы")}
        description={uiText("Профили сотрудников, навыки, опыт и участие в аудитах. Откройте карточку, чтобы посмотреть подробности.")}
        aside={(isAdmin || isAuditor) ? (
          <div className="workspace-side-actions">
            <button type="button" className="btn btn-outline" onClick={handleEditMyProfile}>
              <UserCheck size={15}/>{uiText(" Мой профиль аудитора")}</button>
          </div>
        ) : null}
      />

      {error && <div className="alert alert-error" style={{ marginBottom:16 }}>{error}</div>}

      {/* Stats */}
      <div className="stats-grid" style={{ marginBottom:16 }}>
        <div className="stat-card"><div className="stat-value">{summary.total}</div><div className="stat-label">{uiText("Всего аудиторов")}</div></div>
        <div className="stat-card"><div className="stat-value" style={{ color:'#1d4ed8' }}>{summary.staff}</div><div className="stat-label">{uiText("Штатных")}</div></div>
        <div className="stat-card"><div className="stat-value" style={{ color:'#a16207' }}>{summary.contracted}</div><div className="stat-label">{uiText("Привлекаемых")}</div></div>
        <div className="stat-card"><div className="stat-value" style={{ color:'#16a34a' }}>{summary.available}</div><div className="stat-label">{uiText("Указали доступность")}</div></div>
      </div>

      {/* Filters */}
      <div className="card" style={{ marginBottom:16 }}>
        <div className="card-body auditor-pool-filters" style={{ display:'flex', gap:12, flexWrap:'wrap', alignItems:'center' }}>
          <div className="search-bar" style={{ flex:1, minWidth:220 }}>
            <Search size={15} className="search-bar-icon"/>
            <input placeholder={uiText("Поиск по имени, специализации, должности…")} value={search} onChange={e=>setSearch(e.target.value)}/>
          </div>
          <div className="ap-control-options">
            {['all','staff','contracted'].map(t=>(
              <button type="button" key={t} className="ap-button ap-button--secondary ap-control-action" aria-pressed={typeFilter===t} onClick={()=>setTypeFilter(t)}>
                {t==='all'?uiText("Все"):t==='staff'?uiText("Штатные"):uiText("Привлекаемые")}
              </button>
            ))}
          </div>
          <select className="form-control" style={{ width:'auto', minWidth:200 }} value={specFilter} onChange={e=>setSpecFilter(e.target.value)}>
            <option value="">{uiText("Все специализации")}</option>
            {SPECIALIZATIONS.map(s=><option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
          {(search||typeFilter!=='all'||specFilter) && (
            <button type="button" className="ap-button ap-button--secondary ap-control-action" onClick={()=>{setSearch('');setTypeFilter('all');setSpecFilter('');}}>
              <X size={14}/>{uiText(" Сбросить")}</button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="loading-center"><div className="spinner"/></div>
      ) : filtered.length===0 ? (
        <div className="empty-state">
          <Users size={36} color="#ddd"/>
          <h3>{uiText("Аудиторов не найдено")}</h3>
          <p>{uiText("Попробуйте изменить фильтры")}</p>
        </div>
      ) : (
        <div>
          {/* Group: staff first */}
          {['staff','contracted'].map(type=>{
            const group = filtered.filter(p=>p.auditor_type===type);
            if (!group.length) return null;
            const typeInfo = typeLabel(type);
            return (
              <div key={type} style={{ marginBottom:24 }}>
                <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:12 }}>
                  <span style={{ fontSize:13, fontWeight:700, color:typeInfo.color }}>{typeInfo.label}</span>
                  <span style={{ fontSize:12, background:typeInfo.bg, color:typeInfo.color, borderRadius:999, padding:'2px 8px', fontWeight:600 }}>{group.length}</span>
                </div>
                {group.map(p=>(
                  <AuditorCard key={p.user_id} profile={p} audits={audits} assignmentBusy={assignmentBusy}
                    onOpen={openProfile}
                    onAssign={handleAssign} canManage={isAdmin}
                    currentUserId={user?.id} onApply={handleApply}/>
                ))}
              </div>
            );
          })}
        </div>
      )}

      {/* Profile edit modal */}
      {editingProfile && (
        <ProfileModal profile={editingProfile} onClose={()=>setEditingProfile(null)} onSaved={()=>{setEditingProfile(null);load();}}/>
      )}
    </div>
  );
}
